import { Grid, Stack, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { AxiosError } from 'axios';
import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  getDomainType,
  signupCreateAccount,
  loginUser,
  verifyEmail,
  amplitude,
} from 'wisestep-api';
import {
  SignupForm,
  WsTypography,
  WsTypoColors,
  WsTypoVariants,
  PersonalEmailWarning,
  TextOnLine,
  WsColors,
  Item,
  WisestepLogo,
} from 'wisestep-base';

import { WsLink, AuthLayout } from '../../../components';
import { getSocialIcon } from '../../../utils/getSocialIcon';
import ISignUpParams from '../types/ISignupParams';
import { socialSignUp } from '../utils/common';

const SignupButton = styled(Button)(() => ({
  color: WsColors.GenericRichBlack,
}));

const RegisterForm = () => {
  useEffect(() => {
    const script = document.createElement('script');
    script.innerHTML =
      "{(function (w, d, s, l, i) {w[l] = w[l] || [];w[l].push({'gtm.start': new Date().getTime(),event: 'gtm.js',});const f = d.getElementsByTagName(s)[0],j = d.createElement(s),dl = l != 'dataLayer' ? '&l=' + l : '';j.async = true;j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;f.parentNode.insertBefore(j, f);})(window, document, 'script', 'dataLayer', 'GTM-WV5JJJX')}";
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userObject = {
    email: '',
    login: '',
    name: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    source: 'website',
    type: 'company',
    password: '',
    invitationKey: null,
    langKey: 'en',
    activated: false,
  };
  const [isPersonalEmail, setIsPersonalEmail] = useState(false);
  const stateRef = useRef<ISignUpParams>();
  const [resetForm, setResetForm] = useState<boolean>(false);
  const [errorProp, setErrorProp] = useState<{
    field: string;
    message: string;
    isError: boolean;
  }>({
    field: '',
    message: '',
    isError: false,
  });

  const onSubmit = async (data: ISignUpParams) => {
    const userData = data;
    userData.login = data.email;
    userData.firstName = data.name.split(' ')[0];
    userData.lastName = data.name.split(' ')[1];
    stateRef.current = userData;
    const domainType = await getDomainType(data.email);
    const domainTypeRes: { free: boolean } = domainType.data;
    if (domainTypeRes && domainTypeRes['free']) {
      userData.type = 'personal';
      stateRef.current = userData;
      setIsPersonalEmail(true);
    } else {
      userData.type = 'company';
      saveAccount();
    }
  };

  const saveAccount = async () => {
    const captchaToken: string = await (window as any).grecaptcha.execute(
      '6LdUkecUAAAAALaOhBIvGcQ1L7k6Eaufmhjy4zGw',
      {
        action: 'register',
      }
    );
    const userData: ISignUpParams = stateRef.current ? stateRef.current : userObject;
    try {
      const validate = await validateEmail(userData.email);
      userData.activated = validate;
    } catch (e) {
      userData.activated = false;
    }

    try {
      userData.invitationKey = null;
      userData.captchaResponse = captchaToken;
      userData.langKey = 'en';
      if (
        userData.email === 'admin@integrationwp.com' ||
        userData.email === 'user@integrationwp.com'
      ) {
        userData.source = 'extension';
        userData.activated = true;
        userData.type = 'company';
      }
      const account: { data: string } = await signupCreateAccount(userData);
      if (account.data === 'activated') {
        await loginUser({
          username: userData.email,
          password: userData.password,
          loginUsing: 'website',
          overrideExistingToken: true,
          rememberMe: true,
          captchaResponse: captchaToken,
        });
        navigate('/workspace/initiate');
      } else {
        navigate('/verify-account', {
          state: {
            email: userData.email,
          },
        });
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleError = (errStatus: string) => {
    let message = '';
    switch (errStatus) {
      case 'login already in use':
        message = 'Login name already registered! Please choose another one.';
        break;
      case 'email address already in use':
        message = 'E-mail is already in use! Please choose another one.';
        break;
      case 'email validation failed':
        message = 'Your email is invalid.';
        break;
      default:
        message = 'Some error occurred. Please try later';
        break;
    }
    setErrorProp({
      field: 'email',
      message: message,
      isError: true,
    });
  };

  const validateEmail = async (email: string) => {
    try {
      await verifyEmail(email);
      setErrorProp({
        field: 'email',
        message: '',
        isError: false,
      });
      return true;
    } catch (e) {
      amplitude('Signup failed', {
        clickedOn: 'Signup_failed',
        pageName: 'Signup',
        pageUrl: window.location.href,
        interfaceType: 'Web',
      });
      const err = e as AxiosError;
      if (err.response?.data === 'unable to validate email') {
        setErrorProp({
          field: 'email',
          message: '',
          isError: false,
        });
        return false;
      } else {
        handleError(err.response?.data);
      }
    }
    return false;
  };

  const closePopup = () => {
    setResetForm(true);
    setIsPersonalEmail(false);
  };

  return (
    <AuthLayout>
      <Item>
        <Stack alignItems="center" spacing={4} direction="column">
          <WisestepLogo />
          <Grid container justifyContent="center" spacing={1}>
            <Grid item>
              <WsTypography variant={WsTypoVariants.TITLE}>Are you a</WsTypography>
            </Grid>
            <Grid item>
              <WsTypography variant={WsTypoVariants.TITLE} bold>
                Recruiter?
              </WsTypography>
            </Grid>
            <Grid item xs={12} ml={23}>
              <WsTypography variant={WsTypoVariants.TITLE}>Sign up for an account</WsTypography>
            </Grid>
          </Grid>
        </Stack>
        <Grid mt={3} container justifyContent="center" alignItems="center">
          <Grid item>
            <Item>
              <SignupButton
                onClick={() => {
                  socialSignUp('Google');
                  amplitude('Signup using Google', {
                    clickedOn: 'Signup_Google',
                    pageName: 'Signup',
                    pageUrl: window.location.href,
                    interfaceType: 'Web',
                  });
                }}
                startIcon={getSocialIcon('Google')}
                variant="outlined"
                size="large"
              >
                Sign in with Google
              </SignupButton>
            </Item>
          </Grid>
          <Grid item>
            <Item>
              <SignupButton
                onClick={() => {
                  socialSignUp('Outlook');
                  amplitude('Signup using Microsoft', {
                    clickedOn: 'Signup_Microsoft',
                    pageName: 'Signup',
                    pageUrl: window.location.href,
                    interfaceType: 'Web',
                  });
                }}
                startIcon={getSocialIcon('Microsoft')}
                variant="outlined"
                size="large"
              >
                Sign in with Microsoft
              </SignupButton>
            </Item>
          </Grid>
        </Grid>
        <TextOnLine>OR</TextOnLine>
        <SignupForm
          validateEmail={validateEmail}
          onSubmit={onSubmit}
          resetForm={resetForm}
          errorProp={errorProp}
          emailParam={searchParams.get('email')}
          amplitudeHandler={() =>
            amplitude('Signup Form Button', {
              clickedOn: 'Signup_Button',
              pageName: 'Signup',
              pageUrl: window.location.href,
              interfaceType: 'Web',
            })
          }
        />
        <PersonalEmailWarning
          closePopup={closePopup}
          saveAccount={saveAccount}
          open={isPersonalEmail}
        />
        <Stack justifyContent="center" alignItems="center" direction="row" spacing={1}>
          <WsTypography variant={WsTypoVariants.BODY1}>Already have an account ?</WsTypography>
          <WsLink to="/">
            <WsTypography
              variant={WsTypoVariants.BODY1}
              color={WsTypoColors.PRIMARY}
              cursor="pointer"
            >
              Log in here
            </WsTypography>
          </WsLink>
        </Stack>
      </Item>
    </AuthLayout>
  );
};

export default RegisterForm;
